import React, { useEffect, useState } from "react";
import {
  getAcceptingInsurance,
  getProviderPricing,
  updateAcceptingInsurance,
  upsertProviderPricing,
} from "../APIs/providerApi";
import { showToast } from "../APIs/utils/toastManager";

const APPOINTMENT_TYPES = ["NEW_PATIENT", "FOLLOW_UP", "TELEHEALTH"];

const defaultRow = () => ({ fullPrice: "", depositAmount: "" });

export default function PricingTab({ providerId }) {
  const [pricing, setPricing] = useState({
    NEW_PATIENT: defaultRow(),
    FOLLOW_UP: defaultRow(),
    TELEHEALTH: defaultRow(),
  });
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acceptingInsurance, setAcceptingInsurance] = useState(true);
  const [savingInsurance, setSavingInsurance] = useState(false);

  useEffect(() => {
    Promise.all([
      getProviderPricing(providerId),
      getAcceptingInsurance(providerId),
    ])
      .then(([rows, insuranceSetting]) => {
        const map = {
          NEW_PATIENT: defaultRow(),
          FOLLOW_UP: defaultRow(),
          TELEHEALTH: defaultRow(),
        };
        rows.forEach((r) => {
          if (map[r.appointmentType]) {
            map[r.appointmentType] = {
              fullPrice: r.fullPrice ?? "",
              depositAmount: r.depositAmount ?? "",
            };
          }
        });
        setPricing(map);
        setAcceptingInsurance(insuranceSetting.acceptingInsurance);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [providerId]);

  const handleInsuranceToggle = async () => {
    const next = !acceptingInsurance;
    try {
      setSavingInsurance(true);
      const result = await updateAcceptingInsurance(providerId, next);
      setAcceptingInsurance(result.acceptingInsurance);
      showToast("Insurance preference saved.", "success");
    } catch {
      showToast("Failed to save insurance preference.", "error");
    } finally {
      setSavingInsurance(false);
    }
  };

  const handleChange = (type, field, value) => {
    setPricing((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleSave = async (type) => {
    const row = pricing[type];
    const full = parseFloat(row.fullPrice);
    const deposit = parseFloat(row.depositAmount);

    if (isNaN(full) || full <= 0)
      return showToast("Enter a valid full price.", "error");
    if (isNaN(deposit) || deposit <= 0)
      return showToast("Enter a valid deposit.", "error");
    if (deposit > full)
      return showToast("Deposit cannot exceed full price.", "error");

    try {
      setSaving(type);
      await upsertProviderPricing(providerId, {
        appointmentType: type,
        fullPrice: full,
        depositAmount: deposit,
      });
      showToast("Pricing saved.", "success");
    } catch {
      showToast("Failed to save pricing.", "error");
    } finally {
      setSaving(null);
    }
  };

  if (loading)
    return <p className="text-sm text-gray-500">Loading pricing...</p>;

  return (
    <div className="max-w-xl space-y-6">
      <h2 className="text-lg font-semibold text-gray-800">
        Appointment Pricing
      </h2>
      <p className="text-sm text-gray-500">
        Set full price and deposit per appointment type. Self-pay patients pay
        the deposit via Stripe at booking; the remainder is collected at the
        visit.
      </p>

      {APPOINTMENT_TYPES.map((type) => (
        <div
          key={type}
          className="bg-white border border-gray-200 rounded-lg p-5 space-y-3"
        >
          <h3 className="text-sm font-semibold text-gray-700">
            {type.replace("_", " ")}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Full Price ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={pricing[type].fullPrice}
                onChange={(e) =>
                  handleChange(type, "fullPrice", e.target.value)
                }
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                placeholder="e.g. 150"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Deposit ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={pricing[type].depositAmount}
                onChange={(e) =>
                  handleChange(type, "depositAmount", e.target.value)
                }
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                placeholder="e.g. 50"
              />
            </div>
          </div>
          <button
            onClick={() => handleSave(type)}
            disabled={saving === type}
            className="text-sm bg-teal-600 hover:bg-teal-700 text-white px-4 py-1.5 rounded transition disabled:opacity-50"
          >
            {saving === type ? "Saving..." : "Save"}
          </button>
        </div>
      ))}

      <div className="bg-white border border-gray-200 rounded-lg p-5 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">
            Accepting Insurance
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Turn this off to offer self-pay appointments only.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={acceptingInsurance}
          aria-label="Accepting Insurance"
          disabled={savingInsurance}
          onClick={handleInsuranceToggle}
          className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
            acceptingInsurance ? "bg-teal-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 mt-1 rounded-full bg-white shadow transition-transform ${
              acceptingInsurance ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </div>
  );
}
